import cronParser from 'cron-parser';
import moment from 'moment-timezone';

import {
  INTERVAL_YEAR,
  INTERVAL_MONTH,
  INTERVAL_WEEK,
  INTERVAL_DAY,
  INTERVAL_HOUR,
  MINUTE_STEP,
  MIN_MINUTE_GAP,
  paramsToCron,
} from './helpers';

const toSortedMinuteNumbers = (minutes) => {
  const list = Array.isArray(minutes)
    ? minutes
    : minutes == null || minutes === ''
    ? []
    : [minutes];
  return [
    ...new Set(
      list
        .filter((m) => m !== '' && m != null)
        .map(Number)
        .filter((n) => Number.isFinite(n)),
    ),
  ].sort((a, b) => a - b);
};

/**
 * True when the given hours contain two entries that are numerically
 * adjacent (including the 23 -> 0 day wrap), meaning the same minute
 * selection repeats close enough across the hour boundary to collide.
 */
export const hasAdjacentHours = (hours) => {
  const nums = [
    ...new Set(
      (Array.isArray(hours) ? hours : [])
        .filter((h) => h !== '' && h != null)
        .map(Number)
        .filter((n) => Number.isFinite(n)),
    ),
  ].sort((a, b) => a - b);
  if (nums.length < 2) {
    return false;
  }
  for (let i = 1; i < nums.length; i += 1) {
    if (nums[i] - nums[i - 1] === 1) {
      return true;
    }
  }
  return nums[0] === 0 && nums[nums.length - 1] === 23;
};

/**
 * Error message when minutes are off the 5-minute grid or closer than 15 minutes.
 * Hourly schedules also treat 55 → 00 as a 5-minute wrap, which is too close.
 */
export const getMinutesScheduleError = (
  minutes,
  { wrapAround = false } = {},
) => {
  const nums = toSortedMinuteNumbers(minutes);
  if (!nums.length) {
    return undefined;
  }
  if (nums.some((n) => n < 0 || n > 59 || n % MINUTE_STEP !== 0)) {
    return 'Minutes must be in 5-minute increments (0, 5, 10, …, 55)';
  }
  for (let i = 1; i < nums.length; i += 1) {
    if (nums[i] - nums[i - 1] < MIN_MINUTE_GAP) {
      return 'Selected minutes must be at least 15 minutes apart (e.g. 5, 20, 35)';
    }
  }
  if (wrapAround && nums.length >= 2) {
    const wrapGap = nums[0] + 60 - nums[nums.length - 1];
    if (wrapGap < MIN_MINUTE_GAP) {
      return 'Selected minutes must be at least 15 minutes apart (e.g. 5, 20, 35)';
    }
  }
  return undefined;
};

/**
 * Retry delays must stay in increasing order: each one > the previous
 * (Retry 2 cannot be the same as or less than Retry 1, and so on).
 */
export const getRetryMinutesOrderError = (retryMinutes) => {
  const list = Array.isArray(retryMinutes) ? retryMinutes : [];
  if (list.length < 2) {
    return undefined;
  }
  for (let i = 1; i < list.length; i += 1) {
    const prev = Number(list[i - 1]);
    const curr = Number(list[i]);
    if (!Number.isFinite(prev) || !Number.isFinite(curr) || curr <= prev) {
      return `Retry ${i + 1} must be more than Retry ${i}`;
    }
  }
  return undefined;
};

export const getRetryRowError = (retryMinutes, idx, { maxMinutes } = {}) => {
  if (idx < 1) {
    const list = Array.isArray(retryMinutes) ? retryMinutes : [];
    const curr = Number(list[idx]);
    if (
      idx === 0 &&
      Number.isFinite(maxMinutes) &&
      Number.isFinite(curr) &&
      curr > maxMinutes
    ) {
      return `Must be before the next scheduled send (max ${maxMinutes} min)`;
    }
    return null;
  }
  const list = Array.isArray(retryMinutes) ? retryMinutes : [];
  const prev = Number(list[idx - 1]);
  const curr = Number(list[idx]);
  if (!Number.isFinite(prev) || !Number.isFinite(curr) || curr <= prev) {
    return `Must be more than Retry ${idx} (${prev} min)`;
  }
  if (Number.isFinite(maxMinutes) && curr > maxMinutes) {
    return `Must be before the next scheduled send (max ${maxMinutes} min)`;
  }
  return null;
};

export const RETRY_MINUTE_STEP = MINUTE_STEP;
export const MAX_RETRY_MINUTES = 24 * RETRY_MINUTE_STEP;
/**
 * How many retries Mail Setup lets an admin add. This is a UI cap only:
 * it stops new configs from growing past it, but nothing rewrites configs
 * already stored, and CustomReportMailRetry's getConfiguredRetryMinutes
 * still schedules every entry it finds. A config written before this cap
 * (or by anything other than this form) can therefore still run more.
 */
export const MAX_NUMBER_OF_RETRY = 3;

/**
 * Advisory notice for a stored config that predates the cap. Deliberately
 * NOT wired into getMailConfigMinimumError: an existing published report
 * with 4+ retries would otherwise fail getPdfReportsScheduleError and block
 * saving the whole project from unrelated wizard tabs, with no migration
 * and no way to fix it except hand-editing that one report.
 */
export const getRetryMinutesOverCapNotice = (retryMinutes) => {
  const list = Array.isArray(retryMinutes) ? retryMinutes : [];
  if (list.length > MAX_NUMBER_OF_RETRY) {
    return `This report has ${list.length} retries, more than the current limit of ${MAX_NUMBER_OF_RETRY}. All ${list.length} still run until they are removed.`;
  }
  return undefined;
};

const getMailCrontab = (mailConfig = {}) => {
  if (mailConfig.crontab) {
    return mailConfig.crontab;
  }
  if (!mailConfig.interval) {
    return null;
  }
  return paramsToCron({
    interval: mailConfig.interval,
    minutes: mailConfig.minutes || [],
    hours: mailConfig.hours || [],
    daysOfMonth: mailConfig.daysOfMonth || [],
    months: mailConfig.months || [],
    daysOfWeek: mailConfig.daysOfWeek || [],
  });
};

const cronRepeatGapCache = new Map();
const MAX_CRON_GAP_CACHE = 50;

/**
 * Smallest gap in minutes between consecutive crontab fires.
 * Uses cron-parser the same way the mail worker does (prev slot + next slots).
 * Returns null when the schedule cannot be parsed.
 */
export const getCronRepeatGapMinutes = (mailConfig = {}) => {
  const crontab = getMailCrontab(mailConfig);
  if (!crontab) {
    return null;
  }
  if (cronRepeatGapCache.has(crontab)) {
    return cronRepeatGapCache.get(crontab);
  }
  let gapMinutes = null;
  try {
    // +1 second matches shouldRunCrontab so exact boundary times map to
    // the current slot rather than the previous one.
    const checkTimeForPrev = new Date(Date.now() + 1000);
    const expression = cronParser.parseExpression(crontab, {
      currentDate: checkTimeForPrev,
    });
    const scheduledTime = expression.prev().toDate();
    const nextExpression = cronParser.parseExpression(crontab, {
      currentDate: new Date(scheduledTime.getTime() + 1000),
    });
    let prevTime = scheduledTime;
    let minGapMs = Infinity;
    for (let i = 0; i < 12; i += 1) {
      const nextTime = nextExpression.next().toDate();
      minGapMs = Math.min(minGapMs, nextTime.getTime() - prevTime.getTime());
      prevTime = nextTime;
    }
    const gap = Math.round(minGapMs / 60000);
    gapMinutes = gap > 0 ? gap : null;
  } catch (err) {
    gapMinutes = null;
  }
  if (cronRepeatGapCache.size >= MAX_CRON_GAP_CACHE) {
    const oldest = cronRepeatGapCache.keys().next().value;
    cronRepeatGapCache.delete(oldest);
  }
  cronRepeatGapCache.set(crontab, gapMinutes);
  return gapMinutes;
};

/** Largest retry delay that still fires before the next crontab slot. */
export const getMaxRetryMinutesForSchedule = (mailConfig = {}) => {
  const gap = getCronRepeatGapMinutes(mailConfig);
  if (gap == null) {
    return MAX_RETRY_MINUTES;
  }
  const maxStep = Math.floor((gap - 1) / RETRY_MINUTE_STEP) * RETRY_MINUTE_STEP;
  return Math.max(0, Math.min(MAX_RETRY_MINUTES, maxStep));
};

export const formatCronRepeatGap = (gapMinutes) => {
  if (!Number.isFinite(gapMinutes) || gapMinutes <= 0) {
    return null;
  }
  if (gapMinutes < 60) {
    return `${gapMinutes} minutes`;
  }
  if (gapMinutes % 60 === 0 && gapMinutes < 24 * 60) {
    const hours = gapMinutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (gapMinutes % (24 * 60) === 0) {
    const days = gapMinutes / (24 * 60);
    return days === 1 ? '1 day' : `${days} days`;
  }
  return `${gapMinutes} minutes`;
};

export const clampRetryMinutes = (retryMinutes, maxMinutes) => {
  if (!Number.isFinite(maxMinutes) || maxMinutes <= 0) {
    return [];
  }
  const next = [];
  (Array.isArray(retryMinutes) ? retryMinutes : []).forEach((value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) {
      return;
    }
    const stepped =
      Math.floor(Math.min(n, maxMinutes) / RETRY_MINUTE_STEP) *
      RETRY_MINUTE_STEP;
    if (stepped > 0 && (next.length === 0 || stepped > next[next.length - 1])) {
      next.push(stepped);
    }
  });
  return next;
};

export const getRetryMinutesScheduleError = (retryMinutes, mailConfig = {}) => {
  const list = Array.isArray(retryMinutes)
    ? retryMinutes.map(Number).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  if (!list.length) {
    return undefined;
  }
  const maxMinutes = getMaxRetryMinutesForSchedule(mailConfig);
  if (maxMinutes <= 0) {
    return 'This schedule repeats too often for retries. Increase the gap between scheduled sends.';
  }
  if (list.some((n) => n > maxMinutes)) {
    return `Retry delays must finish before the next scheduled send (max ${maxMinutes} min)`;
  }
  return undefined;
};

/**
 * Split a stored (UTC) datetime into date (YYYY-MM-DD) and time (HH:mm:ss)
 * as they'd read on a wall clock in `timezone` — the project's timezone, not
 * the browser's. Without `timezone`, falls back to the browser's local time
 * (only for callers that haven't been updated to pass one).
 */
export const splitMailDateTime = (value, timezone) => {
  if (!value) {
    return { date: '', time: '' };
  }
  const m = timezone ? moment.tz(value, timezone) : moment(value);
  if (!m.isValid()) {
    return { date: '', time: '' };
  }
  return {
    date: m.format('YYYY-MM-DD'),
    time: m.format('HH:mm:ss'),
  };
};

/**
 * Combine a date + time — entered on a wall clock in `timezone` (the
 * project's timezone, not the browser's) — into an absolute UTC ISO
 * datetime string. Without `timezone`, falls back to the browser's local
 * time (only for callers that haven't been updated to pass one) — this was
 * the pre-existing bug: an admin's browser timezone could silently differ
 * from the project's, shifting the actual send window.
 */
export const combineMailDateTime = (date, time, timezone) => {
  if (!date) {
    return null;
  }
  const raw = String(time || '').trim();
  const normalized = !raw ? '00:00:00' : raw.length === 5 ? `${raw}:00` : raw;
  const combined = `${date}T${normalized}`;
  const m = timezone ? moment.tz(combined, timezone) : moment(combined);
  if (!m.isValid()) {
    return null;
  }
  return m.toISOString();
};

export const nowMailDateTime = () => new Date().toISOString();

/**
 * End must be after start when both are set. Missing start/end is allowed
 * so existing reports without a window still save.
 */
export const getScheduleWindowError = (mailConfig = {}) => {
  const start = mailConfig.startDateTime
    ? new Date(mailConfig.startDateTime)
    : null;
  const end = mailConfig.endDateTime ? new Date(mailConfig.endDateTime) : null;
  if (start && Number.isNaN(start.getTime())) {
    return 'Start date time is invalid';
  }
  if (end && Number.isNaN(end.getTime())) {
    return 'End date time is invalid';
  }
  if (start && end && end.getTime() <= start.getTime()) {
    return 'End date time must be after start date time';
  }
  return undefined;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidFailureNotificationEmail = (email) =>
  EMAIL_PATTERN.test(String(email || '').trim());

export const getFailureNotificationEmailsError = (
  failureNotificationEmails,
) => {
  const list = Array.isArray(failureNotificationEmails)
    ? failureNotificationEmails
        .map((email) => String(email || '').trim())
        .filter(Boolean)
    : [];
  if (!list.length) {
    return undefined;
  }
  const invalid = list.find((email) => !isValidFailureNotificationEmail(email));
  if (invalid) {
    return `Invalid email address: ${invalid}`;
  }
  return undefined;
};

/** Manual recipient tags must be valid email addresses. Variable IDs are skipped. */
export const getRecipientEmailsError = (mailConfig = {}) => {
  const recipientType = mailConfig.recipientType || 'assignee';
  if (recipientType !== 'manual') {
    return undefined;
  }
  const target = mailConfig.recipient && mailConfig.recipient.target;
  const list = Array.isArray(target)
    ? target.map((email) => String(email || '').trim()).filter(Boolean)
    : typeof target === 'string' && target.trim()
    ? [target.trim()]
    : [];
  if (!list.length) {
    return undefined;
  }
  const invalid = list.find((email) => !isValidFailureNotificationEmail(email));
  if (invalid) {
    return `Invalid email address: ${invalid}`;
  }
  return undefined;
};

const isEmptySelection = (value) => {
  if (value == null || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};

const isMailPdfReportType = (reportType) =>
  reportType === 'eapps-alert-digest-mail' ||
  reportType === 'general-report-mail';

/**
 * Minimum Mail Setup fields required for the selected repeat frequency,
 * plus the 15-minute gap rule on minutes.
 */
export const getMailConfigMinimumError = (mailConfig = {}) => {
  const interval = mailConfig.interval;
  if (!interval) {
    return 'Repeat frequency is required';
  }
  if (interval === INTERVAL_YEAR && isEmptySelection(mailConfig.months)) {
    return 'Month(s) are required';
  }
  if (
    (interval === INTERVAL_YEAR || interval === INTERVAL_MONTH) &&
    isEmptySelection(mailConfig.daysOfMonth)
  ) {
    return 'Day(s) of month are required';
  }
  if (
    (interval === INTERVAL_YEAR ||
      interval === INTERVAL_MONTH ||
      interval === INTERVAL_WEEK) &&
    isEmptySelection(mailConfig.daysOfWeek)
  ) {
    return 'Day(s) of week are required';
  }
  if (
    (interval === INTERVAL_YEAR ||
      interval === INTERVAL_MONTH ||
      interval === INTERVAL_WEEK ||
      interval === INTERVAL_DAY) &&
    isEmptySelection(mailConfig.hours)
  ) {
    return 'Hour(s) of day are required';
  }
  if (
    interval === INTERVAL_YEAR ||
    interval === INTERVAL_MONTH ||
    interval === INTERVAL_WEEK ||
    interval === INTERVAL_DAY ||
    interval === INTERVAL_HOUR
  ) {
    if (isEmptySelection(mailConfig.minutes)) {
      return 'Minutes are required';
    }
    const minutesError = getMinutesScheduleError(mailConfig.minutes, {
      wrapAround:
        interval === INTERVAL_HOUR || hasAdjacentHours(mailConfig.hours),
    });
    if (minutesError) {
      return minutesError;
    }
  }

  const retryError = getRetryMinutesOrderError(mailConfig.retryMinutes);
  if (retryError) {
    return retryError;
  }
  const retryScheduleError = getRetryMinutesScheduleError(
    mailConfig.retryMinutes,
    mailConfig,
  );
  if (retryScheduleError) {
    return retryScheduleError;
  }

  const failureEmailsError = getFailureNotificationEmailsError(
    mailConfig.failureNotificationEmails,
  );
  if (failureEmailsError) {
    return failureEmailsError;
  }

  const recipientType = mailConfig.recipientType || 'assignee';
  if (recipientType === 'manual' || recipientType === 'variable') {
    const target = mailConfig.recipient && mailConfig.recipient.target;
    const hasTarget = Array.isArray(target)
      ? target.length > 0
      : !isEmptySelection(target);
    if (!hasTarget) {
      return 'Email recipients are required';
    }
  }
  const recipientEmailsError = getRecipientEmailsError(mailConfig);
  if (recipientEmailsError) {
    return recipientEmailsError;
  }

  const windowError = getScheduleWindowError(mailConfig);
  if (windowError) {
    return windowError;
  }

  return undefined;
};

const flattenPdfSections = (sections = []) => {
  const result = [];
  (sections || []).forEach((section) => {
    if (!section) {
      return;
    }
    result.push(section);
    if (section.sections && section.sections.length) {
      result.push(...flattenPdfSections(section.sections));
    }
  });
  return result;
};

/** Section-level checks: published content, chart IDs, digest section. */
export const getPdfReportContentError = (report) => {
  if (!report) {
    return undefined;
  }
  const allSections = flattenPdfSections(report.sections);
  if (report.published && allSections.length === 0) {
    return 'Add at least one section';
  }
  const chartWithoutId = allSections.find(
    (section) =>
      section.type === 'chart' && !String(section.value || '').trim(),
  );
  if (chartWithoutId) {
    return 'Chart ID is required';
  }
  if (
    report.published &&
    report.reportType === 'eapps-alert-digest-mail' &&
    !allSections.some((section) => section.type === 'alerts-digest')
  ) {
    return 'Add an Alert Digest section';
  }
  return undefined;
};

/** Minimum fields for a single Custom Report (name, type, mail setup, content). */
export const getPdfReportMinimumError = (
  report,
  { withLabel = true, contentError } = {},
) => {
  if (!report) {
    return 'Report is incomplete';
  }
  const reportLabel = report.name && String(report.name).trim();
  if (!reportLabel) {
    return 'Report name is required';
  }
  const prefix = withLabel ? `Report "${reportLabel}": ` : '';
  if (!report.reportType) {
    return `${prefix}Report type is required`;
  }
  if (!report.useAllMilestones && isEmptySelection(report.milestones)) {
    return `${prefix}Select at least one milestone`;
  }
  // Same gating as getPdfReportContentError below: an unpublished/draft
  // report is a work-in-progress and shouldn't block saving the rest of the
  // project just because its Mail Setup isn't filled in yet.
  if (report.published && isMailPdfReportType(report.reportType)) {
    const mailError = getMailConfigMinimumError(report.mailConfig || {});
    if (mailError) {
      return `${prefix}${mailError}`;
    }
  }

  // Callers iterating many reports per render (e.g. a sidebar list next to
  // a full editor) can pass in a contentError they've already computed
  // once, instead of re-walking the section tree here.
  const finalContentError =
    contentError !== undefined
      ? contentError
      : getPdfReportContentError(report);
  return finalContentError ? `${prefix}${finalContentError}` : undefined;
};

/** First minimum-level validation error across all Custom Reports. */
export const getPdfReportsScheduleError = (pdfReports = []) => {
  if (!Array.isArray(pdfReports)) {
    return undefined;
  }
  let firstError;
  pdfReports.some((report) => {
    firstError = getPdfReportMinimumError(report);
    return !!firstError;
  });
  return firstError;
};
